import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import NFTCard from "@/components/NFTCard";
import CollectionCard from "@/components/CollectionCard";
import HitCounter from "@/components/HitCounter";
import { Sparkles, Store, Library, ArrowRight, Hammer, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";

export default function Home() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    api.get("/marketplace", { params: { limit: 8 } }).then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/collections", { params: { limit: 6 } }).then(({ data }) => setCollections(data)).catch(() => {});
    api.get("/trending/creators", { params: { limit: 8 } }).then(({ data }) => setCreators(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
      {/* Hero window */}
      <div className="card-window">
        <div className="window-title">
          <span className="text-xs font-display">🌐 cure8r.com / home.html</span>
          <span className="text-[10px] font-mono">_ □ X</span>
        </div>
        <div className="px-6 md:px-10 pt-6 pb-10 bg-[#fffae6]">
          <div className="text-center">
            <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl md:text-6xl">
              <span className="glitter-text">Mint what you make.</span>
              <br />
              <span className="text-shadow-90 text-retro-navy">Own it</span>{" "}
              <span className="font-pixel text-5xl md:text-7xl text-retro-hot">FOR LIFE!</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg font-serif90 italic">
              Where <b>students</b>, <b>teachers</b> &amp; <b>school</b> can mint, own &amp;
              sell what they produce.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/marketplace" data-testid="hero-browse-market-btn" className="btn-90 btn-90-primary text-base px-6 py-3 no-underline">
                <Store size={16} /> Enter the Market!
              </Link>
              {!user && (
                <Link to="/signup" data-testid="hero-signup-btn" className="btn-90 text-base px-6 py-3 no-underline">
                  <Sparkles size={16} /> Sign Up FREE
                </Link>
              )}
              <Link to="/collections" data-testid="hero-collections-btn" className="btn-90 btn-90-link text-base px-6 py-3 no-underline">
                <Library size={16} /> Collections
              </Link>
            </div>
            <div className="mt-8 flex justify-center">
              <HitCounter />
            </div>
            <div className="mt-2 text-[11px] font-mono text-black/70">
              <span className="blink">★</span> Best viewed at 800×600 in Netscape Navigator <span className="blink">★</span>
            </div>
          </div>

          {/* Feature pill row */}
          <div className="divider-90 mt-10" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            {[
              { t: "WalletConnect™", d: "ERC-721 + ERC-1155 — your art lives on the blockchain", c: "#ff00ff" },
              { t: "Teacher Verified", d: "Assignments minted with classroom approval", c: "#0066ff" },
              { t: "Profit Split: 97 / 1 / 1 / 1", d: "Creator keeps 97%. School + Teacher + Cure8r split 3%.", c: "#008800" },
            ].map((x) => (
              <div key={x.t} className="card-90 p-4">
                <div className="font-display font-bold text-lg" style={{ color: x.c }}>★ {x.t} ★</div>
                <div className="font-serif90 text-sm mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trending creators */}
      {creators.length > 0 && (
        <section className="mt-10 card-window">
          <div className="window-title">
            <span className="text-xs font-display">🏆 TopCreators.exe</span>
            <span className="text-[10px] font-mono">_ □ X</span>
          </div>
          <div className="p-5 bg-[#c0c0c0]">
            <h2 className="font-display font-bold text-2xl">
              <span className="rainbow-text">TRENDING CREATORS</span>
            </h2>
            <div className="text-xs font-mono italic mt-1">~ Top makers right now ~</div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {creators.map((c, i) => {
                const isImg = (c.preview_type || "").startsWith("image/");
                return (
                  <Link
                    key={c.id}
                    to={`/profile/${c.id}`}
                    data-testid={`trending-creator-${c.id}`}
                    className="card-90 p-2 block no-underline text-black"
                    style={{ textDecoration: "none" }}
                  >
                    <div className="relative aspect-square bevel-in overflow-hidden bg-white">
                      {isImg ? (
                        <img src={fileUrl(c.preview_path)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full checkers grid place-items-center">
                          <div className="bg-white border-2 border-black px-2 py-1 font-display font-bold text-xl">#{i + 1}</div>
                        </div>
                      )}
                      <span className="absolute top-1 left-1 tag-90 tag-90-hot">#{i + 1}</span>
                    </div>
                    <div className="font-display font-bold text-sm truncate mt-2">{c.name}</div>
                    <div className="text-[11px] font-mono truncate">{c.school_name || "—"}</div>
                    <div className="text-[10px] font-mono mt-1 flex justify-between">
                      <span>♥ {c.mints}m</span>
                      <span>$ {c.sales}s</span>
                      <span>Ξ{c.volume_eth || 0}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured market */}
      <section className="mt-10 card-window">
        <div className="window-title">
          <span className="text-xs font-display">🛒 JustMinted.html</span>
          <span className="text-[10px] font-mono">_ □ X</span>
        </div>
        <div className="p-5 bg-[#c0c0c0]">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display font-bold text-2xl text-retro-hot">★ JUST MINTED ★</h2>
              <div className="text-xs font-mono italic">~ Fresh on the chain ~</div>
            </div>
            <Link to="/marketplace" data-testid="home-view-all-link" className="text-retro-link underline font-display flex items-center gap-1">
              View all the things! <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divider-90" />
          {featured.length === 0 ? (
            <div className="card-90 p-8 text-center">
              <div className="font-display font-bold text-lg">No public mints yet!</div>
              <div className="font-serif90 italic">Be the first to drop something.</div>
              {!user && (
                <Link to="/signup" className="btn-90 btn-90-primary mt-4 inline-flex no-underline">
                  Get started →
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((a) => <NFTCard key={a.id} asset={a} />)}
            </div>
          )}
        </div>
      </section>

      {/* Collections */}
      <section className="mt-10 card-window">
        <div className="window-title">
          <span className="text-xs font-display">📚 Collections.dir</span>
          <span className="text-[10px] font-mono">_ □ X</span>
        </div>
        <div className="p-5 bg-[#c0c0c0]">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display font-bold text-2xl text-retro-navy">FEATURED COLLECTIONS</h2>
              <div className="text-xs font-mono italic">~ Curated drops ~</div>
            </div>
            <Link to="/collections" className="text-retro-link underline font-display flex items-center gap-1">
              All collections <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divider-90" />
          {collections.length === 0 ? (
            <div className="card-90 p-8 text-center font-serif90 italic">
              No collections yet — teachers, curate the first one!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {collections.map((c) => <CollectionCard key={c.id} collection={c} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA + webring footer */}
      <section className="mt-10 grid md:grid-cols-3 gap-5">
        <div className="card-window md:col-span-2">
          <div className="window-title">
            <span className="text-xs font-display">📢 join_us.html</span>
            <span className="text-[10px] font-mono">_ □ X</span>
          </div>
          <div className="p-8 bg-[#fffae6] text-center">
            <h3 className="font-display font-bold text-3xl">
              <span className="glitter-text">Your art deserves to outlive school!</span>
            </h3>
            <p className="font-serif90 mt-3 max-w-md mx-auto">
              Sign up with your school email, mint your first asset, keep it forever.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              {!user ? (
                <Link to="/signup" data-testid="cta-signup-btn" className="btn-90 btn-90-primary text-base px-6 py-3 no-underline">
                  <Hammer size={14} /> Create account
                </Link>
              ) : (
                <Link to="/dashboard" data-testid="cta-signup-btn" className="btn-90 btn-90-primary text-base px-6 py-3 no-underline">
                  <Hammer size={14} /> Mint Something
                </Link>
              )}
              <Link to="/register-school" data-testid="cta-school-btn" className="btn-90 text-base px-6 py-3 no-underline">
                Register your school
              </Link>
            </div>
          </div>
        </div>
        <div className="card-window">
          <div className="window-title">
            <span className="text-xs font-display">📬 webring.html</span>
            <span className="text-[10px] font-mono">_ □ X</span>
          </div>
          <div className="p-3 bg-[#c0c0c0]">
            <div className="web-ring text-center">
              <div className="font-display font-bold text-lg">★ CURE8R WEBRING ★</div>
              <div className="text-xs mt-1">{`<<`} prev | random | next {`>>`}</div>
            </div>
            <div className="mt-3 card-90 p-3 text-center">
              <Mail size={18} className="inline mb-1" />
              <div className="font-serif90 text-sm">Email the webmaster:</div>
              <a href="mailto:webmaster@cure8r.com" className="font-mono text-xs">webmaster@cure8r.com</a>
            </div>
            <div className="mt-3 text-[10px] font-mono text-center">
              Copyright © 2026 Cure8r.<br />All rights reserved.<br />Made on a 56k modem.
            </div>
          </div>
        </div>
      </section>

      <div className="text-center mt-10 mb-4 text-[11px] font-mono opacity-80">
        ★ This site is optimized for the World Wide Web ★
      </div>
    </div>
  );
}
